import { Reveal, Overline } from "@/components/Reveal";
import { scrollToId } from "@/lib/lenis";
import { ArrowRight } from "@phosphor-icons/react";

const TEAM_IMG =
  "https://images.unsplash.com/photo-1622675363311-3e1904dc1885?auto=format&fit=crop&w=1400&q=80";

const WHAT_WE_DO = [
  {
    title: "Manager Development",
    body: "Being a great employee doesn't automatically make someone a great manager. We help new and experienced managers strengthen their leadership skills, navigate difficult conversations, coach employees, address performance concerns, and lead with greater confidence and accountability.",
  },
  {
    title: "Workplace Skills Labs",
    body: "Our interactive workshops help employees and leaders build the skills that make working together easier and more effective. From professional communication and conflict management to accountability and workplace relationships, we turn everyday challenges into opportunities to learn and improve.",
  },
  {
    title: "HR Consulting",
    body: "From employee relations and performance management to workplace concerns and HR process improvement, we provide practical guidance to help organizations navigate people-related challenges with greater clarity and consistency.",
  },
  {
    title: "Operations Consulting",
    body: "Strong teams need strong processes. We help organizations identify opportunities to improve workflows, clarify responsibilities, strengthen accountability, and create more consistent day-to-day operations.",
  },
];

const ChapterNumber = ({ num }) => (
  <div className="md:col-span-4">
    <div className="md:sticky md:top-32">
      <Reveal>
        <span className="font-serif italic text-[5rem] md:text-[7rem] leading-none text-terracotta/90 select-none">
          {num}
        </span>
      </Reveal>
    </div>
  </div>
);

const Manifesto = () => (
  <section id="about" data-testid="about-section" className="bg-cream py-24 md:py-32 px-6 md:px-10">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24">
        <Reveal className="md:col-span-4">
          <Overline>Our Story</Overline>
        </Reveal>
        <Reveal delay={0.1} className="md:col-span-8">
          <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl leading-tight text-ink max-w-3xl">
            Not Your Average{" "}
            <span className="font-serif italic font-medium text-terracotta">HR &amp; Operations Partner.</span>
          </h2>
        </Reveal>
      </div>

      <div className="space-y-20 md:space-y-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12">
          <ChapterNumber num="01" />
          <div className="md:col-span-8 max-w-2xl">
            <Reveal delay={0.1}>
              <h3 className="font-display font-bold tracking-tight text-2xl md:text-3xl text-ink mb-5">
                Better managers. Stronger teams. Healthier workplaces.
              </h3>
              <div className="space-y-5 text-base md:text-lg leading-relaxed text-ink-soft">
                <p>At Network In Colors, we believe the way people work together matters just as much as the work itself.</p>
                <p>
                  Businesses need more than policies, procedures, and people in leadership positions. They need
                  managers who know how to lead, teams that communicate effectively, and workplace practices that
                  help people do their best work.
                </p>
                <p className="font-semibold text-ink">That's where we come in.</p>
                <p>
                  Network In Colors is a Dallas–Fort Worth-based HR and operations consulting firm that helps
                  organizations develop stronger managers, build practical workplace skills, and create more
                  effective day-to-day operations. Through engaging workshops, leadership development, and
                  customized consulting, we help businesses address real workplace challenges with solutions that
                  can be applied immediately.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-8 md:col-start-5">
            <Reveal delay={0.15}>
              <div className="overflow-hidden rounded-[2rem] h-64 md:h-96">
                <img
                  src={TEAM_IMG}
                  alt="Diverse group of professionals collaborating around a table"
                  data-testid="about-team-image"
                  className="w-full h-full object-cover object-[50%_22%] hover:scale-105 transition-transform duration-700"
                />
              </div>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12">
          <ChapterNumber num="02" />
          <div className="md:col-span-8 max-w-2xl">
            <Reveal delay={0.1}>
              <h3 className="font-display font-bold tracking-tight text-2xl md:text-3xl text-ink mb-5">
                What We Do
              </h3>
              <p className="text-base md:text-lg leading-relaxed text-ink-soft mb-10">
                We focus on the skills, systems, and leadership practices that keep workplaces moving forward.
              </p>
              <div className="space-y-8">
                {WHAT_WE_DO.map((w) => (
                  <div key={w.title} className="border-l-2 border-terracotta pl-6">
                    <h4 className="font-display font-bold tracking-tight text-lg md:text-xl text-ink mb-2">
                      {w.title}
                    </h4>
                    <p className="text-sm md:text-base leading-relaxed text-ink-soft">{w.body}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12">
          <ChapterNumber num="03" />
          <div className="md:col-span-8 max-w-2xl">
            <Reveal delay={0.1}>
              <h3 className="font-display font-bold tracking-tight text-2xl md:text-3xl text-ink mb-5">
                Who We Work With
              </h3>
              <div className="space-y-5 text-base md:text-lg leading-relaxed text-ink-soft">
                <p>
                  We partner with businesses and organizations that recognize the value of investing in their
                  people and improving how their teams operate.
                </p>
                <p>
                  Whether you're preparing employees for their first management role, helping experienced leaders
                  sharpen their skills, addressing recurring workplace challenges, or looking for more effective HR
                  and operational practices, we work with you to identify what your organization needs and develop
                  an approach that fits.
                </p>
                <p>
                  Our goal isn't to deliver a one-size-fits-all solution. It's to help you build capabilities that
                  last beyond the workshop or consulting engagement.
                </p>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12">
          <ChapterNumber num="04" />
          <div className="md:col-span-8 max-w-2xl">
            <Reveal delay={0.1}>
              <h3 className="font-display font-bold tracking-tight text-2xl md:text-3xl text-ink mb-5">
                Let's Make Work Better.
              </h3>
              <div className="space-y-5 text-base md:text-lg leading-relaxed text-ink-soft">
                <p>A stronger workplace starts with the people in it.</p>
                <p>
                  If you're ready to develop your managers, strengthen workplace skills, or find practical
                  solutions to your HR and operations challenges, let's talk about what that could look like for
                  your organization.
                </p>
                <p className="font-serif italic text-ink">
                  Network In Colors. Not Your Average HR &amp; Operations Partner.
                </p>
              </div>
              <button
                data-testid="about-cta-connect"
                onClick={() => scrollToId("#contact")}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink text-white font-semibold px-8 py-4 text-sm md:text-base hover:bg-terracotta hover:scale-105 transition-[background-color,transform] duration-300"
              >
                Let's Connect
                <ArrowRight size={18} weight="bold" />
              </button>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default Manifesto;
